import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Target, Shield, Users, Lightbulb, CheckCircle, TrendingUp, Award, Lock, Zap, Heart, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const About = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-theme/5 via-background to-blue-theme/10 py-16 md:py-24">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <Badge variant="outline" className="mb-4">About Circle Pro Hub</Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-foreground">
              Empowering Real Estate Agents to Work Smarter
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Circle is the trusted marketplace where real estate agents discover, compare, and purchase verified tools—all backed by AI-powered recommendations and real agent insights.
            </p>
          </div>
        </section>

        {/* Company Stats Section */}
        <section className="py-12 md:py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-5xl mx-auto">
              <Card className="text-center border-2 hover:border-blue-theme/50 transition-colors">
                <CardContent className="pt-8 pb-8">
                  <div className="text-4xl md:text-5xl font-bold text-blue-theme mb-2">50+</div>
                  <div className="text-muted-foreground font-medium">Verified Vendors</div>
                </CardContent>
              </Card>
              
              <Card className="text-center border-2 hover:border-blue-theme/50 transition-colors">
                <CardContent className="pt-8 pb-8">
                  <div className="text-4xl md:text-5xl font-bold text-blue-theme mb-2">1,000+</div>
                  <div className="text-muted-foreground font-medium">Tools & Services</div>
                </CardContent>
              </Card>
              
              <Card className="text-center border-2 hover:border-blue-theme/50 transition-colors">
                <CardContent className="pt-8 pb-8">
                  <div className="text-4xl md:text-5xl font-bold text-blue-theme mb-2">100%</div>
                  <div className="text-muted-foreground font-medium">Agent-Focused</div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Our Story Section */}
        <section className="py-12 md:py-20 bg-secondary/20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <Target className="h-8 w-8 text-blue-theme" />
                <h2 className="text-3xl md:text-4xl font-bold text-foreground">Our Story</h2>
              </div>
              <div className="space-y-6 text-muted-foreground text-lg leading-relaxed">
                <p>
                  Real estate agents face a fragmented vendor landscape. Dozens of CRM platforms, lead generation tools, marketing services, and physical products compete for attention—but it's nearly impossible to know which ones actually deliver ROI.
                </p>
                <p>
                  Agents waste hours testing tools they'll never adopt, comparing confusing pricing models, and wondering if there's a better option they haven't discovered yet.
                </p>
                <p>
                  <strong className="text-foreground">Circle solves this problem.</strong> We've built the first centralized marketplace exclusively for real estate agents—where every vendor is verified, every product is vetted by real users, and AI-powered recommendations guide you to the right tools for your business.
                </p>
                <p>
                  No more guesswork. No more wasted budget. Just clarity, confidence, and better decisions.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Our Values Section */}
        <section className="py-12 md:py-20 bg-background">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-foreground">Our Values</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {/* Trust */}
              <Card className="border-2 hover:border-blue-theme/50 hover:shadow-lg transition-all">
                <CardContent className="pt-8 pb-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-theme/10 flex items-center justify-center">
                    <Shield className="h-8 w-8 text-blue-theme" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-foreground">Trust</h3>
                  <p className="text-muted-foreground text-sm">
                    Every vendor is verified. Every product is vetted. We protect agents with rigorous quality standards.
                  </p>
                </CardContent>
              </Card>

              {/* Transparency */}
              <Card className="border-2 hover:border-blue-theme/50 hover:shadow-lg transition-all">
                <CardContent className="pt-8 pb-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-theme/10 flex items-center justify-center">
                    <Lightbulb className="h-8 w-8 text-blue-theme" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-foreground">Transparency</h3>
                  <p className="text-muted-foreground text-sm">
                    Real reviews. Real pricing. Real results. No hidden fees or misleading claims.
                  </p>
                </CardContent>
              </Card>

              {/* Agent-First */}
              <Card className="border-2 hover:border-blue-theme/50 hover:shadow-lg transition-all">
                <CardContent className="pt-8 pb-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-theme/10 flex items-center justify-center">
                    <Users className="h-8 w-8 text-blue-theme" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-foreground">Agent-First</h3>
                  <p className="text-muted-foreground text-sm">
                    Built by agents, for agents. Every decision prioritizes what helps you close more deals.
                  </p>
                </CardContent>
              </Card>

              {/* Innovation */}
              <Card className="border-2 hover:border-blue-theme/50 hover:shadow-lg transition-all">
                <CardContent className="pt-8 pb-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-theme/10 flex items-center justify-center">
                    <Zap className="h-8 w-8 text-blue-theme" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-foreground">Innovation</h3>
                  <p className="text-muted-foreground text-sm">
                    AI-powered recommendations that learn from top agents to guide your next move.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Why Trust Circle Section */}
        <section className="py-12 md:py-20 bg-gradient-to-br from-blue-theme/5 to-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-8 justify-center">
                <Award className="h-8 w-8 text-blue-theme" />
                <h2 className="text-3xl md:text-4xl font-bold text-foreground">Why Trust Circle?</h2>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="border-2">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-success/10 flex items-center justify-center">
                        <CheckCircle className="h-6 w-6 text-success" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-2 text-foreground">Rigorous Vendor Verification</h3>
                        <p className="text-muted-foreground text-sm">
                          Every vendor undergoes a thorough vetting process. We verify business credentials, product quality, and customer support standards before approval.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-success/10 flex items-center justify-center">
                        <TrendingUp className="h-6 w-6 text-success" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-2 text-foreground">Real Agent Results</h3>
                        <p className="text-muted-foreground text-sm">
                          Product ratings and reviews come from verified agents who actually use the tools. No fake reviews or inflated ratings.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-success/10 flex items-center justify-center">
                        <Lock className="h-6 w-6 text-success" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-2 text-foreground">Secure Transactions</h3>
                        <p className="text-muted-foreground text-sm">
                          SSL encryption, PCI compliance, and 256-bit security protect every purchase. Your data is safe with us.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-success/10 flex items-center justify-center">
                        <Heart className="h-6 w-6 text-success" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg mb-2 text-foreground">Buyer Protection</h3>
                        <p className="text-muted-foreground text-sm">
                          If a product doesn't match its description or fails to deliver, we're here to help resolve issues and protect your investment.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-24 bg-gradient-to-br from-blue-theme via-blue-theme-dark to-blue-theme text-white">
          <div className="container mx-auto px-4 text-center max-w-3xl">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Ready to find your next tool?
            </h2>
            <p className="text-lg md:text-xl mb-8 text-white/90">
              Join thousands of agents who trust Circle to power their business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/browse">
                <Button size="lg" className="bg-primary-button hover:bg-primary-button/90 text-foreground font-bold px-8 w-full sm:w-auto">
                  Shop Agent Tools <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/sell">
                <Button size="lg" variant="outline" className="bg-white/10 border-white/30 hover:bg-white/20 text-white font-semibold px-8 w-full sm:w-auto">
                  Become a Vendor
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default About;
